import { useEffect, useMemo, useRef, useState } from "react";
import { Layers, Pill, Download } from "lucide-react";
import api from "@/lib/api";
import { CATEGORY_META, BASEMAP_OPTIONS } from "@/data/mapConfig";
import { PHARMACY_COLORS, SERVICED_COUNT } from "@/data/pharmacyStates";
import Header from "@/components/Header";
import Sidebar from "@/components/Sidebar";
import MapView from "@/components/MapView";
import { StateToggle } from "@/components/StateToggle";
import maplibregl from "maplibre-gl";

const FILTER_ORDER = ["pain_management", "chiropractic", "diagnostic_imaging", "neurology_tbi", "telemed", "other_services", "pharmacy"];

function milesBetween(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 3958.8;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Group each category by practice (company), then sort alphabetically by city within a practice,
// and assign a gap-free 1..N number in that grouped order.
function renumberByCategory(locs) {
  const cityKey = (l) => (l.city || l.name || "").toLowerCase();
  const compKey = (l) => (l.company || l.name || "").toLowerCase();
  const byCat = {};
  locs.forEach((l) => {
    (byCat[l.category] = byCat[l.category] || []).push(l);
  });
  const numById = {};
  Object.values(byCat).forEach((arr) => {
    [...arr]
      .sort(
        (a, b) =>
          compKey(a).localeCompare(compKey(b)) ||
          cityKey(a).localeCompare(cityKey(b)) ||
          (a.name || "").localeCompare(b.name || "")
      )
      .forEach((l, i) => {
        numById[l.id] = i + 1;
      });
  });
  return locs.map((l) => ({ ...l, number: numById[l.id] }));
}

export default function MapPage() {
  const [locations, setLocations] = useState([]);
  const [activeCats, setActiveCats] = useState(new Set(FILTER_ORDER));
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [basemap, setBasemap] = useState("light");
  const [pharmacyMode, setPharmacyMode] = useState(false);
  const [zipPoint, setZipPoint] = useState(null);
  const [stateFilter, setStateFilter] = useState("all");
  const [focusBounds, setFocusBounds] = useState(null);
  const zipCacheRef = useRef({});

  const handleExport = () => {
    const a = document.createElement("a");
    a.href = "/GOAT_Provider_Network_Map.pdf";
    a.download = "GOAT_Provider_Network_Map.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    const z = new URLSearchParams(window.location.search).get("zip");
    if (z && /^\d{5}$/.test(z)) setSearch(z);
  }, []);

  useEffect(() => {
    api.get("/locations").then((res) => setLocations(res.data)).catch(() => {});
  }, []);

  // Resolve a 5-digit ZIP search to a map point (free Zippopotam.us lookup)
  const zipQuery = search.trim();
  useEffect(() => {
    const q = zipQuery;
    if (!/^\d{5}$/.test(q)) {
      setZipPoint(null);
      return;
    }
    if (zipCacheRef.current[q]) {
      setZipPoint(zipCacheRef.current[q]);
      return;
    }
    let cancelled = false;
    fetch(`https://api.zippopotam.us/us/${q}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const p = data?.places?.[0];
        if (p) {
          const point = {
            zip: q,
            lat: parseFloat(p.latitude),
            lng: parseFloat(p.longitude),
            city: p["place name"],
            state: p["state abbreviation"],
          };
          zipCacheRef.current[q] = point;
          setZipPoint(point);
        } else {
          setZipPoint(null);
        }
      })
      .catch(() => !cancelled && setZipPoint(null));
    return () => {
      cancelled = true;
    };
  }, [zipQuery]);

  const numbered = useMemo(() => renumberByCategory(locations), [locations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return numbered.filter((l) => {
      if (!activeCats.has(l.category)) return false;
      if (stateFilter !== "all" && l.pinned !== false && l.state !== stateFilter) return false;
      if (!q) return true;
      return (
        l.name.toLowerCase().includes(q) ||
        (l.company || "").toLowerCase().includes(q) ||
        l.city.toLowerCase().includes(q) ||
        l.address.toLowerCase().includes(q) ||
        (l.zip || "").toLowerCase().includes(q)
      );
    });
  }, [numbered, activeCats, search, stateFilter]);

  const nearestMode = !!zipPoint;

  // In ZIP mode: show ALL pinned locations (any category) sorted by distance, with miles.
  const displayed = useMemo(() => {
    if (!nearestMode) return filtered;
    return numbered
      .filter((l) => l.pinned !== false && l.lat && l.lng)
      .map((l) => ({ ...l, _miles: milesBetween(zipPoint, l) }))
      .sort((a, b) => a._miles - b._miles);
  }, [nearestMode, zipPoint, numbered, filtered]);

  const nearest = nearestMode ? displayed[0] || null : null;

  const stateCounts = useMemo(() => {
    const pinned = numbered.filter((l) => l.pinned !== false);
    return { all: pinned.length, TX: pinned.filter((l) => l.state === "TX").length, OK: pinned.filter((l) => l.state === "OK").length };
  }, [numbered]);

  const changeState = (key) => {
    setStateFilter(key);
    setSelected(null);
    const pts = numbered.filter((l) => l.pinned !== false && l.lat && l.lng && (key === "all" || l.state === key));
    if (!pts.length) return;
    const b = new maplibregl.LngLatBounds([pts[0].lng, pts[0].lat], [pts[0].lng, pts[0].lat]);
    pts.forEach((l) => b.extend([l.lng, l.lat]));
    setFocusBounds({ bounds: b, key: Date.now() });
  };

  const toggleCat = (cat) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const counts = useMemo(() => {
    const c = {};
    FILTER_ORDER.forEach((cat) => (c[cat] = numbered.filter((l) => l.category === cat).length));
    return c;
  }, [numbered]);

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-[#0B132B]">
      <Header total={locations.length} />
      <div className="flex-1 flex overflow-hidden">
        <Sidebar
          locations={displayed}
          selected={selected}
          onSelect={setSelected}
          search={search}
          setSearch={setSearch}
          nearestMode={nearestMode}
          zipInfo={zipPoint}
          stateToggle={<StateToggle value={stateFilter} onChange={changeState} counts={stateCounts} className="w-fit" idPrefix="mobile-state-toggle" />}
        />
        <main className="relative flex-1 hidden sm:block">
          <MapView
            locations={displayed}
            selected={selected}
            onSelect={setSelected}
            basemap={basemap}
            pharmacyMode={pharmacyMode}
            searchPoint={nearestMode ? zipPoint : null}
            nearest={nearest}
            focusBounds={focusBounds}
          />

          {!pharmacyMode && (
            <StateToggle
              value={stateFilter}
              onChange={changeState}
              counts={stateCounts}
              className="absolute top-4 left-1/2 -translate-x-1/2 z-10"
            />
          )}

          {/* Floating legend / filter */}
          <div
            data-testid="map-legend"
            className="absolute top-4 left-4 z-10 bg-slate-900/85 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-3.5 w-[240px]"
          >
            {!pharmacyMode ? (
              <>
                <p className="font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 mb-2.5">
                  Provider Categories
                </p>
                <div className="space-y-1.5">
                  {FILTER_ORDER.map((cat) => {
                    const meta = CATEGORY_META[cat];
                    const active = activeCats.has(cat);
                    return (
                      <button
                        key={cat}
                        data-testid={`category-filter-${cat}`}
                        onClick={() => toggleCat(cat)}
                        className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg transition-all duration-200 ${
                          active ? "bg-white/10" : "opacity-45 hover:opacity-80"
                        }`}
                      >
                        <span
                          className="w-3.5 h-3.5 rounded-full shrink-0 border border-white/20"
                          style={{ background: meta.color }}
                        />
                        <span className="text-sm font-medium text-white flex-1 text-left">{meta.label}</span>
                        <span className="text-xs text-slate-400 font-mono">{counts[cat] || 0}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            ) : (
              <>
                <p className="font-heading text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 mb-2.5">
                  Southern Star RX Coverage
                </p>
                <div className="space-y-2 text-sm">
                  <LegendRow color={PHARMACY_COLORS.serviced} label="Serviced state" />
                  <LegendRow color={PHARMACY_COLORS.noCompound} label="Serviced · no compounds" />
                  <LegendRow color={PHARMACY_COLORS.notServiced} label="Not serviced" faded />
                </div>
                <div className="mt-3 pt-3 border-t border-white/10 text-xs text-slate-400">
                  Personal Injury & Workers' Comp pharmacy serving{" "}
                  <span className="text-white font-semibold">{SERVICED_COUNT}</span> states + DC. Free same/next-day delivery.
                </div>
              </>
            )}

            {/* Pharmacy coverage toggle */}
            <button
              data-testid="pharmacy-toggle"
              onClick={() => setPharmacyMode((v) => !v)}
              className={`mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                pharmacyMode
                  ? "bg-indigo-600 hover:bg-indigo-500 text-white"
                  : "bg-white/10 hover:bg-white/20 text-white"
              }`}
            >
              <Pill className="w-4 h-4" />
              {pharmacyMode ? "Back to Provider Map" : "Nationwide Pharmacy Delivery"}
            </button>
          </div>

          {/* Basemap switcher */}
          <div
            data-testid="basemap-switcher"
            className="absolute bottom-4 left-4 z-10 bg-slate-900/85 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl p-1.5 flex items-center gap-1"
          >
            <Layers className="w-4 h-4 text-slate-400 mx-1.5" />
            {BASEMAP_OPTIONS.map((b) => (
              <button
                key={b.key}
                data-testid={`basemap-${b.key}`}
                onClick={() => setBasemap(b.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 ${
                  basemap === b.key ? "bg-white text-[#0B132B]" : "text-slate-300 hover:bg-white/10"
                }`}
              >
                {b.label}
              </button>
            ))}
          </div>
          {/* Download PDF */}
          <button
            data-testid="download-pdf-btn"
            onClick={handleExport}
            className="absolute top-4 right-4 z-10 flex items-center gap-2 bg-[#0B132B]/90 hover:bg-[#0B132B] backdrop-blur-xl border border-white/15 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-2xl transition-all duration-200"
          >
            <Download className="w-4 h-4" />
            Download PDF
          </button>
        </main>
      </div>
    </div>
  );
}

function LegendRow({ color, label, faded }) {
  return (
    <div className={`flex items-center gap-2.5 ${faded ? "opacity-70" : ""}`}>
      <span className="w-3.5 h-3.5 rounded shrink-0 border border-white/20" style={{ background: color }} />
      <span className="text-white">{label}</span>
    </div>
  );
}
